import type { ComponentPropsWithRef, ReactNode } from "react";
import { Fragment, useEffect } from "react";
import { cn } from "@/lib/utils";
import { TruncatedText } from "./TruncatedText";
import {
  tabListClass,
  tabTriggerClass,
  type TabsLayout,
  type TabsVariant,
} from "./tabs-variants";

export type { TabsLayout, TabsVariant };

export type TabConfig<T extends string = string> = {
  id: T;
  label: string;
  description?: string;
  icon?: ReactNode;
  to?: string;
};

export interface TabLinkRenderProps {
  to: string;
  className: string;
  children: ReactNode;
  id?: string;
  "aria-selected": boolean;
  "aria-controls"?: string;
}

type TabsBaseProps<T extends string> = Omit<ComponentPropsWithRef<"div">, "children"> & {
  tabs: TabConfig<T>[];
  activeTab: T;
  variant: TabsVariant;
  layout?: TabsLayout;
  ariaLabel: string;
  idPrefix: string;
};

export type TabsButtonProps<T extends string> = TabsBaseProps<T> & {
  renderTabLink?: never;
  onTabChange: (tab: T) => void;
};

export type TabsLinkProps<T extends string> = TabsBaseProps<T> & {
  renderTabLink: (props: TabLinkRenderProps) => ReactNode;
  onTabChange?: (tab: T) => void;
};

export type TabsProps<T extends string> = TabsButtonProps<T> | TabsLinkProps<T>;

export function Tabs<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  renderTabLink,
  variant,
  layout = "equal",
  ariaLabel,
  idPrefix,
  className,
  ...rest
}: TabsProps<T>) {
  useEffect(() => {
    scrollTabIntoView(`${idPrefix}-tab-${activeTab}`);
  }, [idPrefix, activeTab]);

  return (
    <div
      className={cn(tabListClass(variant, layout), className)}
      style={
        variant === "panel" && layout === "equal"
          ? { gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }
          : undefined
      }
      role="tablist"
      aria-label={ariaLabel}
      {...rest}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const triggerClassName = tabTriggerClass(variant, isActive, layout);
        const tabId = `${idPrefix}-tab-${tab.id}`;
        const controlsId = `${idPrefix}-tabpanel-${tab.id}`;
        const content =
          variant === "panel" ? (
            <PanelTabContent tab={tab} isActive={isActive} />
          ) : (
            <>
              {tab.icon}
              {tab.label}
            </>
          );

        if (tab.to && renderTabLink) {
          return (
            <Fragment key={tab.id}>
              {renderTabLink({
                to: tab.to,
                className: triggerClassName,
                children: content,
                id: tabId,
                "aria-selected": isActive,
                "aria-controls": controlsId,
              })}
            </Fragment>
          );
        }

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={tabId}
            aria-selected={isActive}
            aria-controls={controlsId}
            onClick={() => onTabChange?.(tab.id)}
            className={triggerClassName}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}

/** Keeps the active tab visible when the list scrolls sideways (mobile, deep links). */
function scrollTabIntoView(tabId: string) {
  const tab = document.getElementById(tabId);
  const list = tab?.parentElement;
  if (!tab || !list || list.scrollWidth <= list.clientWidth) {
    return;
  }
  const tabStart =
    tab.getBoundingClientRect().left -
    list.getBoundingClientRect().left +
    list.scrollLeft;
  const tabEnd = tabStart + tab.offsetWidth;
  if (tabStart < list.scrollLeft) {
    list.scrollLeft = tabStart;
  } else if (tabEnd > list.scrollLeft + list.clientWidth) {
    list.scrollLeft = tabEnd - list.clientWidth;
  }
}

function PanelTabContent<T extends string>({
  tab,
  isActive,
}: {
  tab: TabConfig<T>;
  isActive: boolean;
}) {
  return (
    <>
      <div className="flex min-w-0 items-center justify-center gap-2">
        {tab.icon ? (
          <span
            className={cn(
              "shrink-0",
              isActive ? "text-primary-600" : "text-surface-400",
            )}
          >
            {tab.icon}
          </span>
        ) : null}
        <TruncatedText className="font-medium">{tab.label}</TruncatedText>
      </div>
      {tab.description ? (
        <TruncatedText
          className={cn(
            "hidden w-full text-xs leading-snug sm:block",
            isActive ? "text-surface-500" : "text-surface-400",
          )}
        >
          {tab.description}
        </TruncatedText>
      ) : null}
    </>
  );
}
